import { z } from "zod";

/**
 * Analytics environment configuration — validated at module load.
 *
 * FAIL-OPEN CONTRACT (ADR-003 / tech spec §4.2): invalid or missing analytics
 * env vars must NEVER crash the app. Validation failure returns `null`, which
 * turns the entire analytics module into a silent no-op. The absent Client ID
 * is also the deliberate kill switch for rollout/rollback (tech spec §8).
 *
 * CRA note: env vars are baked in at build time; changing them requires a
 * rebuild/redeploy, not a runtime restart.
 */

const analyticsEnvSchema = z.object({
  /** OpenPanel project Client ID for this environment (public, not a secret). */
  clientId: z.string().uuid(),
  /** Ingestion endpoint of the self-hosted OpenPanel instance. */
  apiUrl: z.string().url(),
  /** Environment tag stamped on every event as a cross-check (ADR-002). */
  environment: z.enum(["dev", "uat", "production"]),
});

export type AnalyticsEnv = z.infer<typeof analyticsEnvSchema>;

function readAnalyticsEnv(): AnalyticsEnv | null {
  const parsed = analyticsEnvSchema.safeParse({
    clientId: process.env.REACT_APP_OPENPANEL_CLIENT_ID,
    apiUrl:
      process.env.REACT_APP_OPENPANEL_API_URL ??
      "https://analytics.prodigyhealth.ai/api",
    environment: process.env.REACT_APP_ANALYTICS_ENV,
  });

  if (!parsed.success) {
    // Expected in local dev without a Client ID (kill-switch state) — only
    // warn outside production so misconfiguration is visible to developers.
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(
        "[analytics] disabled — env invalid or unset:",
        parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`)
      );
    }
    return null;
  }

  return parsed.data;
}

/** `null` = analytics disabled (no-op mode). Evaluated once at module load. */
export const analyticsEnv: AnalyticsEnv | null = readAnalyticsEnv();
